import type { OptionDB } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import { InteriorType } from "../../enum/InteriorType";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import { CoverPaddingBuildStep } from "../CoverPaddingBuildStep";
import { FlightCaseBlueprintCalculator } from "../FlightCaseBlueprintCalculator";

/** Usable inner dimensions of a rack interior in mm. */
export interface RackUsableInnerDimensions {
  /** Inner depth (Z) less the thickest lid pad at the front and at the back. */
  depthMm: number;
  /** Inner height (Y) less the thickest lid pad at the top and at the bottom. */
  heightMm: number;
}

/**
 * Usable inner depth and height of a RACK or RACK19 interior: the built inner
 * box less, per side, the thickest pad the cover padding build step lays
 * there. That is the clear space a device has wherever it sits in the
 * compartment. Null for other interiors and for settings the build rejects.
 */
export function calculateRackUsableInnerDimensions(
  optionDb: OptionDB,
  settings: FlightCaseSettings,
): RackUsableInnerDimensions | null {
  const interiorType = settings.interiorOptions?.type;
  if (
    interiorType !== InteriorType.RACK &&
    interiorType !== InteriorType.RACK19
  ) {
    return null;
  }

  const thickest = new Map<Sides, number>();
  let inner: { y: number; z: number };
  try {
    const blueprint = new FlightCaseBlueprintCalculator().execute(settings);
    for (const pad of new CoverPaddingBuildStep(optionDb)
      .selectDependencies(blueprint, settings)
      .values()) {
      thickest.set(
        pad.side,
        Math.max(thickest.get(pad.side) ?? 0, pad.strength),
      );
    }
    inner = blueprint.innerDimensions;
  } catch {
    // Settings the build rejects (e.g. an imported legacy position): show no
    // hint instead of breaking the page; the 3D view reports the build error.
    return null;
  }

  const lid = (side: Sides) => thickest.get(side) ?? 0;
  return {
    depthMm: Math.max(0, inner.z - lid(Sides.FRONT) - lid(Sides.BACK)),
    heightMm: Math.max(0, inner.y - lid(Sides.TOP) - lid(Sides.BOTTOM)),
  };
}
